import { Autocomplete, TextField } from '@mui/material';
import { DateOrTimeView, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { t } from 'i18next';
import moment from 'moment';
import { NativeSelect } from '../../../inputs/NativeSelect';
import { FilterValueInputType } from '../../enum/FilterValueInputType';
import { Operation } from '../../enum/Operation';
import { AutocompleteFilterConfiguration, Expression, FilterConfiguration, Option, SelectFilterConfiguration } from '../../types';

const DATETIME_FIELDS: DateOrTimeView[] = ['year', 'month', 'day', 'hours', 'minutes'];
const TIMESTAMP_FIELDS: DateOrTimeView[] = ['year', 'month', 'day', 'hours', 'minutes', 'seconds'];

type OperatorInputProps = {
  value: Expression['value'];
  operation: Operation;
  filter: FilterConfiguration;
  onChange: (value: Expression['value']) => void;
  errorMessage?: string;
};

/**
 * Depending on the input type, it returns a component function which can then be used for rendering.
 *
 * Use case:
 * const ValueInput = getValueInputComponent(type);
 *
 * ...
 *    <ValueInput ... />
 */
export const getValueInputComponent = (inputType: FilterValueInputType) => {
  const label = t('value');

  switch (inputType) {
    case FilterValueInputType.SELECT:
      return ({ filter, value, onChange, errorMessage }: OperatorInputProps) => {
        const { options } = filter as SelectFilterConfiguration;
        return <NativeSelect options={options} value={value} onChange={onChange} label={label} error={errorMessage} />;
      };
    case FilterValueInputType.AUTOCOMPLETE:
      return ({ filter, value, onChange, errorMessage }: OperatorInputProps) => {
        const { options } = filter as AutocompleteFilterConfiguration;
        return (
          <Autocomplete
            options={options}
            sx={{ '.MuiInputBase-root': { pt: 0 } }}
            value={value as string}
            onChange={(_, value) => {
              // We handle only selection as an Option, no incomplete tags
              if (typeof value === 'string') {
                return;
              }

              onChange((value as Option).value);
            }}
            autoComplete={true}
            autoHighlight={true}
            autoSelect={true}
            disableClearable={true}
            freeSolo={true}
            renderInput={({ inputProps, ...rest }) => (
              <TextField
                {...rest}
                inputProps={{ ...inputProps, 'aria-controls': inputProps['aria-controls'] ?? '' }}
                variant="standard"
                label={label}
                onFocus={(event) => event.target.select()}
                hiddenLabel={true}
                helperText={errorMessage}
                error={!!errorMessage}
              />
            )}
          />
        );
      };
    case FilterValueInputType.DATE_TIME:
    case FilterValueInputType.TIMESTAMP:
      return ({ value: propValue, onChange }: OperatorInputProps) => {
        const value = moment.isMoment(propValue) ? propValue : moment();
        const pickerProps =
          inputType === FilterValueInputType.TIMESTAMP
            ? // Display a ISO format string as input of timestamp fields and set the timezone to UTC
              // to correctly align with the backend +00:00 logic
              { format: 'YYYY-MM-DDTHH:mm:ss+00:00', views: TIMESTAMP_FIELDS, timezone: 'UTC' }
            : { views: DATETIME_FIELDS };
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              {...pickerProps}
              defaultValue={value}
              value={value}
              // The DatePicker does not support milliseconds: remove them from the propagated
              // value to avoid unexpected results
              onChange={(value) => onChange((value ?? moment()).milliseconds(0))}
              label={label}
              slotProps={{
                textField: { variant: 'standard', fullWidth: true, inputProps: { readOnly: true } },
                openPickerButton: { size: 'small' },
              }}
            />
          </LocalizationProvider>
        );
      };
    case FilterValueInputType.STRING:
    default:
      return ({ value, onChange, errorMessage }: OperatorInputProps) => {
        return (
          <TextField
            fullWidth
            variant="standard"
            label={label}
            defaultValue={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
          />
        );
      };
  }
};
